import { Card, DataList, ErrorView, LoadingView, StatusBadge, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { Audio, Button, Label } from '@app/engine';
import { FormatDuration, FormatTimestamp, WrapLTR } from '@shared/common';
import { CallClient } from '@shared/frontend';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/** File-local constants. */
const localConstants = {
  roleCaller: 'caller',
  // A recording stored as a provider URL is playable directly; any other value is an internal storage key with no public address (legacy rows).
  urlPrefix: 'http'
} as const;

/**
 * Call detail screen: transcript, recording, and call summary.
 *
 * @returns The call detail element.
 */
export function CallDetail() {
  const { tenantID } = useDashboard();
  const { selectedLang } = useLocale();
  const [recordingFailed, setRecordingFailed] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const callID = params[Constants.Param.CallID] ?? '';

  const { data, loading, error, reload } = useDashboardData((signal) => CallClient.GetCallDetail(signal, tenantID!, callID, selectedLang), [tenantID, callID, selectedLang]);

  if (loading) return <LoadingView />;
  if (error || !data) return <ErrorView onRetry={reload} />;

  const { call, transcript, outcome, summaryDisplay } = data;

  return (
    <div>
      <Button text={Localized.DashBack} classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.detailBack}`} onClickHandler={() => navigate(Constants.Route.Calls)} />

      <div className={Styles.detailGrid}>
        <div className={Styles.cardStack}>
          <Card title={Localized.CallPanelTitle}>
            <DataList
              rows={[
                { label: Localized.CallsFrom, value: WrapLTR(call.fromE164) },
                { label: Localized.SharedTo, value: call.toE164 },
                { label: Localized.SharedWhen, value: FormatTimestamp(call.startedAt) },
                { label: Localized.CallsDuration, value: FormatDuration(call.durationSeconds) },
                { label: Localized.SharedStatus, hint: Localized.CallsStatusHint, value: <StatusBadge status={call.status} /> },
                { label: Localized.CallsIntent, hint: Localized.CallsIntentHint, value: call.intent ?? '—' },
                { label: Localized.CallsSummary, hint: Localized.CallsSummaryHint, value: summaryDisplay ?? call.summary ?? '—' },
                outcome
                  ? {
                      label: Localized.CallOutcome,
                      hint: Localized.CallOutcomeHint,
                      value: (
                        <>
                          <StatusBadge status={outcome.status} />
                          {(outcome.detail ?? outcome.failureReason) ? <Label text={(outcome.detail ?? outcome.failureReason)!} /> : null}
                        </>
                      )
                    }
                  : null
              ]}
            />
          </Card>
        </div>

        <div className={Styles.cardStack}>
          <Card title={Localized.CallRecording} titleHint={Localized.CallRecordingHint}>
            {call.recordingKey?.startsWith(localConstants.urlPrefix) && !recordingFailed ? (
              // No caption track: the Transcript panel beside this player is the text alternative for the call audio.
              <Audio audioUrl={call.recordingKey} loadingText={Localized.CallRecordingNone} classNameAudio={Styles.audioPlayer} onError={() => setRecordingFailed(true)} />
            ) : (
              // A recording URL that fails to load (expired/removed provider link) reads differently from a call that never produced one: show the "expired" copy only when a URL was actually present.
              <Label
                classNameContainer={Styles.muted}
                text={call.recordingKey?.startsWith(localConstants.urlPrefix) && recordingFailed ? Localized.CallRecordingExpired : Localized.CallRecordingNone}
              />
            )}
          </Card>

          <Card title={Localized.CallTranscript} titleHint={Localized.CallTranscriptHint}>
            {transcript && transcript.turns.length > 0 ? (
              <div className={Styles.transcript}>
                {transcript.turns.map((turn, index) => (
                  <div key={`${turn.startMs}-${index}`} className={`${Styles.turn} ${turn.role === localConstants.roleCaller ? Styles.turnCaller : Styles.turnAgent}`}>
                    <Label classNameContainer={Styles.turnRole} text={turn.role === localConstants.roleCaller ? Localized.CallSpeakerCaller : Localized.CallSpeakerAgent} />
                    <Label text={turn.text} />
                  </div>
                ))}
              </div>
            ) : (
              <Label classNameContainer={Styles.muted} text={Localized.CallTranscriptNone} />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
